import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { SpaceProps } from 'styled-system';
import { space } from 'styled-system';

import { useLocalization } from '../../../providers/LocaleProvider';
import { textWithEllipsis } from '../../../theme/styles';
import { CaptionRegularCR2, HeadlineH9, SubtitleST4 } from '../../Typography';

const BUTLERS = 'BUTLERS';

const nameContainerStyles = { mb: ['sp_8', null, null, 'sp_12'], pb: 'sp_2' };
const nameContainerStylesV2 = { mb: ['sp_4', null, null, 'sp_6'], pb: undefined };

const UnstyledNameContainer = styled.div<SpaceProps>`
  ${space};
`;

const ProductName = styled(HeadlineH9)`
  ${textWithEllipsis(2)};
`;

const productNameStyles: ComponentProps<typeof ProductName> = {
  as: 'p',
  color: 'sys.neutral.text.strong',
  m: 'sp_0',
};

const secondaryTextStyles: ComponentProps<typeof SubtitleST4> = {
  as: 'p',
  m: 'sp_0',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: 'sys.neutral.text.default',
};

export const NameBlock = ({
  isHome24,
  name,
  brandName,
  isMarketplace,
  Typography = ProductName,
  isV2,
}: {
  isHome24: boolean;
  name: string;
  isMarketplace?: boolean;
  brandName?: string;
  Typography?: typeof ProductName;
  isV2?: boolean;
}) => {
  const { t, r } = useLocalization();
  const isButler = brandName && brandName.includes(BUTLERS);
  const getSecondaryText = () => {
    if (!isMarketplace) return brandName;

    if (isHome24) {
      return isButler ? BUTLERS : t('h24_home24');
    }

    return t('h24_marketplace');
  };

  const secondaryText = getSecondaryText();
  const Title = isV2 ? CaptionRegularCR2 : SubtitleST4;
  const containerStyles = isV2 ? nameContainerStylesV2 : nameContainerStyles;

  return (
    <UnstyledNameContainer {...containerStyles}>
      <Typography {...productNameStyles}>{name}</Typography>
      <Title
        {...secondaryTextStyles}
        data-testid={isMarketplace ? 'seller-type' : 'product-brand'}
        {...(secondaryText && {
          'aria-label': r.t('h24_brand_of_the_product', { brand: secondaryText }),
        })}
      >
        {secondaryText}
      </Title>
    </UnstyledNameContainer>
  );
};
